import React from 'react';
import { BsFillPencilFill, BsFillFileEarmarkPlusFill } from 'react-icons/bs';
import { getBase64Img } from '../../../../utils/format';
import { isGenericCylinder, parseObs, isGenericOrder as isGenericOrderUtils } from '../../../../utils/orders';
import { getProductsImages } from '../../../../services/products/productService';
import { usePortalStore } from '../../../../stores/portalStore';
import { ImageSlider } from '../../../../shared/media/slider';
import { Spinner } from '../../../../shared/ui/Spinners';
import { PrimaryTitleGray } from '../../../../app/theme/tokens/colors';
import { TriageField } from '../triage';
import { OrdersDataSource } from '../../../../datasources/orders/OrdersDataSource';
import { UserDataSource } from '../../../../datasources/orders/UsersDataSource';
import { Product } from '../../../../types/catalog/Product';
import { OrderTableData } from '../../../../types/orders/Order';

type OrderDetailsProps = {
    order: OrderTableData;
    onOrderEdit?: (order: OrderTableData) => void;
    uploadCECFile?: (order: OrderTableData) => void;
    handleChangeCECFile?: React.ChangeEventHandler<HTMLInputElement>;
    hiddenFileInput?: React.RefObject<HTMLInputElement>;
    isRowExpanded: boolean;
};

const CAT_ACCESSORIES = 4;

const normalizeAddressValue = (value: unknown) => {
    const normalizedValue = String(value ?? '').trim();

    if (!normalizedValue || ['null', 'undefined'].includes(normalizedValue.toLowerCase())) {
        return '';
    }

    return normalizedValue;
};

const formatOrderAddress = (address: OrderTableData['customer']['address']) => {
    const street = normalizeAddressValue(address?.name);
    const complement = normalizeAddressValue(address?.complement);
    const number = normalizeAddressValue(address?.number);
    const city = normalizeAddressValue(address?.city);
    const uf = normalizeAddressValue(address?.uf);
    const zipcode = normalizeAddressValue(address?.zipcode);

    const streetAndComplement = [street, complement].filter(Boolean).join(' - ');
    const streetAndNumber = [streetAndComplement, number].filter(Boolean).join(', ');
    const cityAndUf = [city, uf].filter(Boolean).join('/');

    return [streetAndNumber, cityAndUf, zipcode ? `CEP: ${zipcode}` : '']
        .filter(Boolean)
        .join('. ') || '-';
};

export const OrderDetails: React.FC<OrderDetailsProps> = ({
    order,
    onOrderEdit,
    uploadCECFile,
    handleChangeCECFile,
    hiddenFileInput,
    isRowExpanded,
}) => {
    // TODO: GET PRODUCT INFO
    const [products, setProducts] = React.useState<{
        accessories: Product[];
        equipments: Product[];
    }>({
        accessories: [],
        equipments: [],
    });
    const [images, setImages] = React.useState<unknown[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [createdByName, setCreatedByName] = React.useState<string>('');
    // Prazo temporariamente oculto nos detalhes do pedido.
    // const [prazo, setPrazo] = React.useState<OrderPrazo | null>(null);
    // const [prazoLoading, setPrazoLoading] = React.useState(false);
    // const [prazoError, setPrazoError] = React.useState(false);

    type CurrentUser = {
        canSeeFullOrdersTable?: boolean;
        isWhiteMartins?: boolean;
        isAdmin?: boolean;
    };

    const currentUser = usePortalStore(
        (state: unknown) => (state as { currentUser: CurrentUser }).currentUser
    );
    const [isGenericOrder, setIsGenericOrder] = React.useState(false);

    const hasGenericAccessory = () => {
        return (
            isGenericOrder &&
            products.accessories.findIndex((x) => isGenericOrderUtils(x.code)) !== -1
        );
    };

    React.useEffect(() => {
        setLoading(true);
        if (isRowExpanded) {
            getOrderProductDetails();
            void resolveCreatedBy();
            // void getOrderPrazo();
        }
    }, [isRowExpanded, order.id, order.created_by]);

    // const getOrderPrazo = async () => {
    //     setPrazo(null);
    //     setPrazoError(false);
    //
    //     const orderId = Number(order.id);
    //     if (!Number.isFinite(orderId)) {
    //         setPrazoError(true);
    //         return;
    //     }
    //
    //     try {
    //         setPrazoLoading(true);
    //         const response = await new OrdersDataSource().getPrazoByOrderId(orderId);
    //         setPrazo(response);
    //     } catch (e) {
    //         console.error(e);
    //         setPrazoError(true);
    //     } finally {
    //         setPrazoLoading(false);
    //     }
    // };
    //
    // const renderPrazoValue = () => {
    //     if (prazoLoading) return 'Carregando...';
    //     if (prazoError || prazo?.prazo_contratual_horas == null) return 'Não disponível';
    //
    //     return `${prazo.prazo_contratual_horas}h`;
    // };

    const resolveCreatedBy = async () => {
        try {
            const raw = String(order.created_by ?? '').trim();
            if (!raw) {
                setCreatedByName('');
                return;
            }

            // Só chama API se parecer email; senão mantém como já veio.
            if (!raw.includes('@')) {
                setCreatedByName(raw);
                return;
            }

            const name = await new UserDataSource().getUserNameByEmail(raw);
            setCreatedByName(name || raw);
        } catch {
            const fallback = String(order.created_by ?? '').trim();
            setCreatedByName(fallback);
        }
    };

    const getOrderProductDetails = async () => {
        try {
            const productsResponse =
                await new OrdersDataSource().getOrderProductDetails(order.number);

            const visibleProducts = (productsResponse ?? []).flatMap((p: Product) => {
                const base = {
                    code: p.code,
                    name: p.name,
                    category: p.category,
                    sub_category: p.sub_category,
                    category_id: p.category_id,
                };

                const lines =
                    Array.isArray(p.lines) && p.lines.length > 0 ? p.lines : null;
                if (lines) {
                    const visibleLine = lines.find((l) => l.kind === 'visible');
                    if (!visibleLine) return [];
                    const qty = Number(visibleLine.quantity ?? 0);
                    if (!Number.isFinite(qty) || qty <= 0) return [];
                    return [{ ...base, quantity: qty, is_exchanged_product: false }];
                }

                if (p.is_exchanged_product) return [];
                return [
                    {
                        ...base,
                        quantity: p.quantity_total ?? p.quantity ?? 0,
                        is_exchanged_product: false,
                    },
                ];
            });

            const modifiedProductsResponse = visibleProducts.map((product) => {
                if (isGenericOrderUtils(product.code)) {
                    setIsGenericOrder(true);
                    return { ...product, name: 'Visita técnica' };
                }
                return product;
            });

            const newProducts = {
                accessories: modifiedProductsResponse.filter(
                    (product) => product.category_id === CAT_ACCESSORIES
                ),
                equipments: modifiedProductsResponse.filter(
                    (product) => product.category_id !== CAT_ACCESSORIES
                ),
            };

            setProducts(newProducts);

            const uniqueCodes = Array.from(
                new Set(modifiedProductsResponse.map((product) => product.code))
            );
            const imagesResponse = await getProductsImages(uniqueCodes);

            setImages(imagesResponse);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (products.equipments.length === 0 && products.accessories.length === 0) {
        return <></>;
    }
    if (loading) {
        return (
            <div
                className="box-expand-panel"
                style={{
                    display: 'block',
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    padding: '50px',
                }}
            >
                <Spinner></Spinner>
            </div>
        );
    }

    return (
        <div
            className="table-expand"
            style={{ display: 'block', boxSizing: 'border-box', overflow: 'hidden' }}
        >
            <div
                style={{
                    display: 'flex',
                    boxSizing: 'border-box',
                    alignItems: 'stretch',
                }}
            >
                <div className="image-equipment box-expand-panel">
                    <div className="img-equip">
                        <ImageSlider images={images.map(getBase64Img)}></ImageSlider>
                    </div>
                </div>
                <div
                    className="box-expand-panel"
                    style={{
                        display: 'block',
                        flex: '1 1 0',
                        minWidth: 0,
                        overflow: 'hidden',
                    }}
                >
                    <div className="equipment-name">
                        {isGenericOrder
                            ? 'Validação e Processamento de pedido'
                            : products.equipments.length === 0
                                ? ''
                                : products.equipments.length > 1
                                    ? 'Equipamentos'
                                    : 'Equipamento'}
                    </div>
                    {products.equipments
                        .filter((equipment) => !equipment.is_exchanged_product)
                        .map(
                            (equipment, idxEq: number) =>
                                !isGenericCylinder(equipment) && (
                                    <div
                                        key={`eq-${idxEq}`}
                                        style={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            minWidth: 0,
                                            color: 'black',
                                            boxSizing: 'border-box',
                                            paddingTop: '15px',
                                        }}
                                    >
                                        <li
                                            style={{
                                                fontSize: '16px',
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            <span style={{ fontWeight: '700' }}>
                                                {currentUser.canSeeFullOrdersTable
                                                    ? `${equipment.code} `
                                                    : ''}
                                            </span>
                                            {!isGenericOrderUtils(equipment.code) &&
                                                !isGenericCylinder(equipment) && (
                                                    <>
                                                        {equipment.quantity}x{' '}
                                                        {equipment.name ?? equipment.sub_category}{' '}
                                                    </>
                                                )}
                                        </li>
                                    </div>
                                )
                        )}
                    {products.equipments.length === 0
                        ? ''
                        : !hasGenericAccessory() && (
                            <React.Fragment>
                                <br />
                                <br />
                            </React.Fragment>
                        )}
                    {!hasGenericAccessory() && (
                        <div className="equipment-name">
                            {products.accessories.length === 0
                                ? ''
                                : products.accessories.length > 1
                                    ? 'Descartáveis'
                                    : 'Descartável'}
                        </div>
                    )}
                    {products.accessories.map((c, idx: number) => (
                        <div
                            key={idx}
                            style={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                color: 'black',
                                boxSizing: 'border-box',
                                paddingTop: '15px',
                            }}
                        >
                            <li
                                style={{
                                    fontSize: '16px',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                }}
                            >
                                <span style={{ fontWeight: '700' }}>
                                    {currentUser.canSeeFullOrdersTable ? `${c.code}: ` : ''}
                                </span>
                                {c.quantity}x {c.name}
                            </li>
                        </div>
                    ))}
                    <br />
                    {order.obs && order.obs.trim() !== '' ? (
                        <>
                            <div className="equipment-name">Observação</div>
                            <div className="description">{parseObs(order.obs, null)}</div>
                            <br />
                        </>
                    ) : (
                        <></>
                    )}
                    {/*
                    <div className="equipment-name">Prazo</div>
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            minHeight: '32px',
                            padding: '6px 12px',
                            border: '1px solid #d8e5df',
                            borderRadius: '6px',
                            background: '#f7fbf9',
                            color: PrimaryTitleGray,
                            fontSize: '14px',
                            fontWeight: 700,
                            boxSizing: 'border-box',
                        }}
                    >
                        {renderPrazoValue()}
                    </div>
                    */}
                </div>

                <div
                    className="box-expand-panel"
                    style={{
                        color: 'black',
                        boxSizing: 'border-box',
                        paddingLeft: '15px',
                        paddingRight: '15px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row-reverse',
                            padding: '0px 10px',
                        }}
                    >
                        {onOrderEdit ? (
                            <BsFillPencilFill
                                style={{
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    color: PrimaryTitleGray,
                                    display: 'block',
                                    margin: '0px 10px',
                                }}
                                onClick={() => onOrderEdit(order)}
                            ></BsFillPencilFill>
                        ) : (
                            <></>
                        )}

                        {uploadCECFile ? (
                            <div style={{ margin: '0px 10px' }}>
                                <input
                                    type="file"
                                    onChange={handleChangeCECFile}
                                    style={{ display: 'none' }}
                                    ref={hiddenFileInput}
                                    multiple
                                />
                                <BsFillFileEarmarkPlusFill
                                    style={{
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        color: PrimaryTitleGray,
                                        display: 'block',
                                    }}
                                    onClick={() => uploadCECFile(order)}
                                ></BsFillFileEarmarkPlusFill>
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                    <div className="equipment-name">Endereço</div>
                    <div className="description">
                        {formatOrderAddress(order.customer.address)}
                    </div>
                    {currentUser.isWhiteMartins || currentUser.isAdmin ? (
                        <TriageField
                            orderId={order.id}
                            height={150}
                            inputField={false}
                            showTitle={true}
                            open={true}
                        ></TriageField>
                    ) : (
                        <></>
                    )}
                    <br />
                    <>
                        <div className="equipment-name">Informações</div>
                        <div className="description">
                            Pedido criado por:
                            <span style={{ fontWeight: '700', marginLeft: '5px' }}>
                                {createdByName || order.created_by}
                            </span>
                        </div>
                    </>
                </div>
            </div>
        </div>
    );
};
